import { useCallback, useMemo, useRef, useState } from "react";
import type { WallOpening } from "../../domain/framing/generateFramedWall";
import {
	matchOpeningSizePreset,
	openingSizePresets,
	resolveDoorSwing,
	resolveHingeSide,
} from "../../domain/openings/openingPresets";
import { validateOpeningSet } from "../../domain/openings/validateOpeningSet";
import type { RoofConfig, SaunaDimensions } from "../../engines/types";
import { useT } from "../../i18n";
import { formatOpeningIssue } from "../../i18n/formatOpeningIssue";
import { useBuilderToast } from "./BuilderToast";

// NOTE: validation here intentionally omits live admin rule overrides — it
// relies on validateOpeningSet's default `{}` (i.e. the
// framing.minCornerClearanceMm rule's *default* value, 150mm). Wiring
// useRulesContext() here would require every test that renders this
// component (and DimensionPanel, which hosts it) to be wrapped in
// <RulesProvider>, which fetches overrides asynchronously — out of scope for
// this change. The 3D drag controller (OpeningDragController) already reads
// live overrides via BuildInput.overrides, which is the interaction that
// matters most for the corner-clearance/knutbräda check.

interface OpeningsEditorProps {
	openings: WallOpening[];
	dimensions: SaunaDimensions;
	partitionSplit?: number;
	allowDividerWall?: boolean;
 hasRelaxRoom?: boolean;
 saunaPosition?: "left" | "right";
 roofConfig?: RoofConfig;
	/** The design's wall height (issue 155); absent = the rule default. */
	wallHeightM?: number;
	onOpeningsChange: (openings: WallOpening[]) => void;
	onStartWindowDrag?: (widthMm: number, heightMm: number) => void;
	isDraggingWindow?: boolean;
}

const DEFAULT_DOOR_OPENING: Omit<WallOpening, "id"> = {
	wall: "front",
	type: "door",
	xMm: 600,
	yMm: 0,
	widthMm: 900,
	heightMm: 1900,
	glazed: true,
};

const CUSTOM_SIZE = "custom";

const DEFAULT_WINDOW_SIZE = {
	widthMm: 900,
	heightMm: 700,
};

function nextOpeningId(): string {
	if ("randomUUID" in crypto) {
		return crypto.randomUUID();
	}
	return `opening-${Date.now()}`;
}

export function OpeningsEditor({
	openings,
	dimensions,
	partitionSplit,
	allowDividerWall = false,
 hasRelaxRoom = allowDividerWall,
 saunaPosition,
 roofConfig,
	wallHeightM,
	onOpeningsChange,
	onStartWindowDrag,
	isDraggingWindow,
}: OpeningsEditorProps) {
	const t = useT();
	const toast = useBuilderToast();
	const [blockedOpeningErrors, setBlockedOpeningErrors] = useState<
		Record<string, string>
	>({});
	const [exitingIds, setExitingIds] = useState<Set<string>>(new Set());
	const newlyAddedRef = useRef<Set<string>>(new Set());

	const getOpeningTypeLabel = (type: WallOpening["type"]): string => {
		return type === "door" ? t.openings_typeDoor : t.openings_typeWindow;
	};

	const openingTitles = useMemo(() => {
		const counts = {
			door: 0,
			window: 0,
		};

		return openings.reduce<Record<string, string>>((titles, opening) => {
			counts[opening.type] += 1;
			titles[opening.id] =
				`${getOpeningTypeLabel(opening.type)} ${counts[opening.type]}`;
			return titles;
		}, {});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [openings, t]);

	const openingErrors = useMemo(() => {
		const validation = validateOpeningSet({
			openings,
			dimensions,
			partitionSplit,
			wallHeightM,
      hasRelaxRoom, saunaPosition, roofConfig,
		});
		const errors: Record<string, string> = {};
		for (const issue of validation.issues) {
			if (issue.openingId && !(issue.openingId in errors)) {
				errors[issue.openingId] = formatOpeningIssue(issue, t);
			}
		}
		return errors;
	}, [openings, dimensions, partitionSplit, hasRelaxRoom, saunaPosition, roofConfig, wallHeightM, t]);

	const applyOpenings = (
		nextOpenings: WallOpening[],
		focusOpeningId?: string,
	): boolean => {
		const validation = validateOpeningSet({
			openings: nextOpenings,
			dimensions,
			partitionSplit,
			wallHeightM,
      hasRelaxRoom, saunaPosition, roofConfig,
		});
		if (!validation.valid) {
			const focusIssue =
				validation.issues.find((issue) => issue.openingId === focusOpeningId) ??
				validation.issues[0];
			if (focusIssue?.openingId) {
				const message = formatOpeningIssue(focusIssue, t);
				setBlockedOpeningErrors((prev) => ({
					...prev,
					[focusIssue.openingId as string]: message,
				}));
			}
			return false;
		}

		setBlockedOpeningErrors({});
		onOpeningsChange(nextOpenings);
		return true;
	};

	const addDoor = () => {
		const opening: WallOpening = {
			id: nextOpeningId(),
			...DEFAULT_DOOR_OPENING,
		};
		const applied = applyOpenings([...openings, opening], opening.id);
		if (applied) {
			newlyAddedRef.current.add(opening.id);
			toast.show(t.toast_doorAdded, "add");
		}
	};

	const handleWindowPlacedToast = useCallback(() => {
		toast.show(t.toast_windowAdded, "add");
	}, [toast, t]);

	// Expose the window toast via the drag callback
	const handleStartWindowDrag = onStartWindowDrag
		? (widthMm: number, heightMm: number) => {
				onStartWindowDrag(widthMm, heightMm);
				// The toast fires later when the window is actually placed —
				// we trigger it from the parent via handleWindowPlacedToast
			}
		: undefined;
	// Make handleWindowPlacedToast available to parent via a side-effect
	// We attach it as a property on the DOM for parents to call, but actually
	// for our implementation we'll just show it when a new window appears
	void handleWindowPlacedToast;

	const updateOpening = (id: string, patch: Partial<WallOpening>) => {
		const nextOpenings = openings.map((opening) =>
			opening.id === id ? { ...opening, ...patch } : opening,
		);
		applyOpenings(nextOpenings, id);
	};

	const removeOpening = (id: string) => {
		const removed = openings.find((o) => o.id === id);
		setExitingIds((prev) => new Set(prev).add(id));

		// Wait for the exit animation to complete before removing from state
		setTimeout(() => {
			setExitingIds((prev) => {
				const next = new Set(prev);
				next.delete(id);
				return next;
			});
			setBlockedOpeningErrors((prev) => {
				const next = { ...prev };
				delete next[id];
				return next;
			});
			onOpeningsChange(openings.filter((opening) => opening.id !== id));

			if (removed) {
				toast.show(
					removed.type === "door" ? t.toast_doorRemoved : t.toast_windowRemoved,
					"remove",
				);
			}
		}, 150);
	};

	// Determine entrance animation class for newly added items
	const getCardClass = (id: string): string => {
		const classes = ["opening-card"];
		if (exitingIds.has(id)) {
			classes.push("opening-card-exit");
		} else if (newlyAddedRef.current.has(id)) {
			classes.push("opening-card-enter");
			// Remove from newly-added set after the animation fires (one-shot)
			requestAnimationFrame(() => {
				newlyAddedRef.current.delete(id);
			});
		}
		return classes.join(" ");
	};

	return (
		<div className="inspector-section-body">
			<div className="opening-editor">
				<div className="opening-editor-intro">
					<p className="section-label">{t.openings_sectionLabel}</p>
					<p className="opening-editor-copy">{t.openings_intro}</p>
				</div>
				<div className="opening-editor-header">
					<button
						type="button"
						className="btn btn-sm opening-action-btn"
						onClick={addDoor}
					>
						{t.openings_addDoor}
					</button>
					{handleStartWindowDrag && (
						<button
							type="button"
							className={`btn btn-sm opening-action-btn${isDraggingWindow ? " is-pending" : ""}`}
							onClick={() =>
								handleStartWindowDrag(
									DEFAULT_WINDOW_SIZE.widthMm,
									DEFAULT_WINDOW_SIZE.heightMm,
								)
							}
							disabled={isDraggingWindow}
						>
							{isDraggingWindow
								? t.openings_placingWindow
								: t.openings_addWindow}
						</button>
					)}
				</div>
				{openings.length === 0 && (
					<p className="text-muted inspector-empty-state">
						{t.openings_emptyState}
					</p>
				)}
				{openings.map((opening) => {
					const openingError =
						blockedOpeningErrors[opening.id] ?? openingErrors[opening.id];
					const openingTitle =
						openingTitles[opening.id] ?? getOpeningTypeLabel(opening.type);
					return (
						<div key={opening.id} className={getCardClass(opening.id)}>
							<div className="opening-card-header">
								<div>
									<h3 className="opening-card-title">{openingTitle}</h3>
									<p className="opening-card-meta">
										{opening.type === "door"
											? t.openings_metaDoor
											: t.openings_metaWindow}
									</p>
								</div>
								<button
									type="button"
									className="btn btn-sm opening-remove-btn"
									onClick={() => removeOpening(opening.id)}
									aria-label={`${t.openings_remove} ${openingTitle}`}
								>
									{t.openings_remove}
								</button>
							</div>
							<div className="opening-grid">
								<label>
									{t.openings_sizePresetLabel}
									<select
										value={matchOpeningSizePreset(opening)?.id ?? CUSTOM_SIZE}
										onChange={(e) => {
											const preset = openingSizePresets(opening.type).find(
												(entry) => entry.id === e.target.value,
											);
											if (preset) {
												updateOpening(opening.id, {
													widthMm: preset.widthMm,
													heightMm: preset.heightMm,
												});
											}
										}}
									>
										{openingSizePresets(opening.type).map((preset) => (
											<option key={preset.id} value={preset.id}>
												{preset.id}
											</option>
										))}
										<option value={CUSTOM_SIZE} disabled>
											{t.openings_sizeCustom}
										</option>
									</select>
								</label>
								{opening.type === "door" && (
									<label>
										{t.openings_doorLeafLabel}
										<select
											value={opening.glazed ? "glass" : "wood"}
											onChange={(e) =>
												updateOpening(opening.id, {
													glazed: e.target.value === "glass",
												})
											}
										>
											<option value="wood">{t.openings_doorLeafWood}</option>
											<option value="glass">{t.openings_doorLeafGlass}</option>
										</select>
									</label>
								)}
								{opening.type === "door" && (
									<label>
										{t.openings_hingeLabel}
										<select
											value={resolveHingeSide(opening)}
											onChange={(e) =>
												updateOpening(opening.id, {
													hingeSide: e.target.value as "left" | "right",
												})
											}
										>
											<option value="left">{t.openings_hingeLeft}</option>
											<option value="right">{t.openings_hingeRight}</option>
										</select>
									</label>
								)}
								{opening.type === "door" && (
									<label>
										{t.openings_swingLabel}
										<select
											value={resolveDoorSwing(opening)}
											onChange={(e) =>
												updateOpening(opening.id, {
													swing: e.target.value as "out" | "in",
												})
											}
										>
											<option value="out">{t.openings_swingOut}</option>
											<option value="in">{t.openings_swingIn}</option>
										</select>
									</label>
								)}
								<label>
									{t.openings_wallLabel}
									<select
										value={opening.wall}
										onChange={(e) =>
											updateOpening(opening.id, {
												wall: e.target.value as WallOpening["wall"],
											})
										}
									>
										<option value="front">{t.openings_wallFront}</option>
										<option value="back">{t.openings_wallBack}</option>
										<option value="left">{t.openings_wallLeft}</option>
										<option value="right">{t.openings_wallRight}</option>
										{allowDividerWall && (
											<option value="divider">{t.openings_wallDivider}</option>
										)}
									</select>
								</label>
								<label>
									X (mm)
									<input
										type="number"
										value={opening.xMm}
										onChange={(e) =>
											updateOpening(opening.id, { xMm: Number(e.target.value) })
										}
									/>
								</label>
								{opening.type === "window" && (
									<label>
										Y (mm)
										<input
											type="number"
											value={opening.yMm}
											onChange={(e) =>
												updateOpening(opening.id, {
													yMm: Number(e.target.value),
												})
											}
										/>
									</label>
								)}
								<label>
									{t.openings_widthLabel}
									<input
										type="number"
										value={opening.widthMm}
										onChange={(e) =>
											updateOpening(opening.id, {
												widthMm: Number(e.target.value),
											})
										}
									/>
								</label>
								<label>
									{t.openings_heightLabel}
									<input
										type="number"
										value={opening.heightMm}
										onChange={(e) =>
											updateOpening(opening.id, {
												heightMm: Number(e.target.value),
											})
										}
									/>
								</label>
							</div>
							{opening.type === "door" &&
								resolveDoorSwing(opening) === "in" && (
									<p className="opening-warning">
										{t.openings_swingInWarning}
									</p>
								)}
							{openingError && <p className="opening-error">{openingError}</p>}
						</div>
					);
				})}
			</div>
		</div>
	);
}
